import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password minimal 6 karakter");
    if (password !== confirm) return toast.error("Konfirmasi password tidak cocok");
    setBusy(true);
    try {
      await api.reset(token, password);
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Token tidak valid atau sudah kedaluwarsa");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto min-h-screen w-full max-w-md lg:max-w-lg bg-white px-6 pt-6" data-testid="reset-page">
      <header className="mb-8 flex items-center gap-2">
        <img src={logo} alt="CeKKeU" data-testid="auth-logo" className="h-14 w-auto" />
      </header>

      {!token && (
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="reset-no-token">
          Tautan reset tidak lengkap. Silakan minta tautan baru dari halaman lupa password.
        </div>
      )}

      {done ? (
        <div className="rounded-3xl border border-zinc-200 bg-white p-6 shield-shadow" data-testid="reset-done">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-extrabold text-zinc-900">Password diperbarui</h1>
          <p className="mt-2 text-sm text-zinc-500">Silakan masuk dengan password baru Anda.</p>
          <Button
            onClick={() => navigate("/login")}
            className="mt-6 h-12 w-full rounded-full bg-zinc-900 text-sm font-semibold hover:bg-zinc-800"
          >
            Ke halaman Masuk
          </Button>
        </div>
      ) : (
        token && (
          <>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-900">Atur ulang password</h1>
            <p className="mt-2 text-sm text-zinc-500">Buat password baru untuk akun Anda.</p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-700 font-heading">Password baru</Label>
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimal 6 karakter"
                  className="h-12 rounded-2xl border-zinc-200 text-base"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-zinc-700 font-heading">Konfirmasi password</Label>
                <Input
                  data-testid="reset-password-confirm-input"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Ulangi password baru"
                  className="h-12 rounded-2xl border-zinc-200 text-base"
                />
              </div>
              <Button
                data-testid="reset-submit-button"
                type="submit"
                disabled={busy}
                className="h-14 w-full rounded-full bg-zinc-900 text-base font-semibold hover:bg-zinc-800"
              >
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Simpan password baru"}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-zinc-500">
              <Link to="/login" className="font-semibold text-emerald-600">
                Kembali ke Masuk
              </Link>
            </p>
          </>
        )
      )}
    </div>
  );
}
